import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import styles from './email-campaign-optimal-sending-panel.module.scss';
import type {MaxOptimalTimePayload, OptimalTimeResponse} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import {Checkbox} from '@Components/checkbox';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import aiGradientSpinner from '@Assets/ai-gradient-spinner.svg';
import {
  getReadableDateForOptimalSending,
  isCurrentTimeEqualToOrGreaterThanOptimalTimeComputed,
  isOptimalSendTimeforScheduledEmailsValid,
} from '@Components/email-marketing-campaigns/components/email-campaign-scheduler-best-time-control/email-campaign-scheduler-best-time-library';
import {mailingListUpdateEvent} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {noop} from '@Utils/general.util';
import {computeClientTimezoneOffset} from '@Utils/date.util';
import {ClickableDiv} from '@Components/clickable-div';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {Tooltip} from '@Components/tooltip';

export interface EmailCampaignOptimalSendingProps {
  emailMarketingCampaignId: string;
  onUpdateSendBestTimeToggle: (updatedValue: boolean) => void;
  prefilledDate?: Date;
  defaultToggleValue?: boolean;
  onUpdateMaxOptimalTime?: (utcTimestamp: number) => void;
}

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 400,
  },
  large: {
    minWidth: 401,
    maxWidth: 1149,
  },
};

export function EmailCampaignOptimalSendingPanel({
  emailMarketingCampaignId,
  onUpdateSendBestTimeToggle,
  prefilledDate,
  defaultToggleValue = false,
  onUpdateMaxOptimalTime = noop,
}: EmailCampaignOptimalSendingProps): ReactElement<any> {
  const [shouldSendAtBestTime, setShouldSendatBestTime] = useState(false);
  const [maxOptimalTime, setMaxOptimalTime] = useState<null | number>(null);
  const [scheduledOptimalSendingError, setScheduledOptimalSendingError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const [isHoveringOnTooltip, setIsHoveringOnTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const sendTimeSupportArticleLink = 'https://support.postermywall.com/hc/en-us/articles/36020881286541-How-to-send-emails-with-the-Send-at-Best-Time-feature-on-PosterMyWall';

  useEffect(() => {
    setShouldSendatBestTime(defaultToggleValue);
  }, [defaultToggleValue]);

  const computeNewOptimalTimeForUnscheduledEmails = (): void => {
    const maxOptimalTimePayload: MaxOptimalTimePayload = {
      idEmailMarketingCampaign: emailMarketingCampaignId,
      clientTimezoneOffset: computeClientTimezoneOffset(),
    };

    window.PMW.writeLocal('emailmarketing/getOptimalSendingTimeForNonScheduledEmails', maxOptimalTimePayload)
      .then((response: OptimalTimeResponse) => {
        setMaxOptimalTime(response.maximumUtcTimestamp);
        onUpdateMaxOptimalTime(response.maximumUtcTimestamp);
      })
      .catch((error: unknown): void => {
        console.log('Error fetching optimal time: ', error);
      });
  };

  const computeNewOptimalTimeForScheduledEmails = (): void => {
    if (!prefilledDate) {
      return;
    }

    const midnightDate = new Date(prefilledDate);
    midnightDate.setHours(0, 0, 0, 0);

    const maxOptimalTimePayload: MaxOptimalTimePayload = {
      idEmailMarketingCampaign: emailMarketingCampaignId,
      clientTimezoneOffset: computeClientTimezoneOffset(),
      scheduledDate: midnightDate.toString(),
    };

    window.PMW.writeLocal('emailmarketing/getOptimalSendingTimeForScheduledEmails', maxOptimalTimePayload)
      .then((response: OptimalTimeResponse) => {
        if (!isOptimalSendTimeforScheduledEmailsValid(response)) {
          setScheduledOptimalSendingError(true);
          setMaxOptimalTime(null);
          onUpdateSendBestTimeToggle(false);
          setShouldSendatBestTime(false);
          return;
        }

        setMaxOptimalTime(response.maximumUtcTimestamp);
        setScheduledOptimalSendingError(false);
        onUpdateMaxOptimalTime(response.maximumUtcTimestamp);
      })
      .catch((error: unknown): void => {
        console.log(error);
      });
  };

  const onMailingListUpdate = (): void => {
    if (!prefilledDate) {
      computeNewOptimalTimeForUnscheduledEmails();
      return;
    }

    computeNewOptimalTimeForScheduledEmails();
  };

  useEffect(() => {
    window.addEventListener(mailingListUpdateEvent, onMailingListUpdate);

    return (): void => {
      window.removeEventListener(mailingListUpdateEvent, onMailingListUpdate);
    };
  }, []);

  useEffect(() => {
    if (maxOptimalTime === null) {
      if (!prefilledDate) {
        computeNewOptimalTimeForUnscheduledEmails();
        return;
      }

      if (!scheduledOptimalSendingError) {
        computeNewOptimalTimeForScheduledEmails();
      }
    }

    const interval = setInterval(() => {
      if (isCurrentTimeEqualToOrGreaterThanOptimalTimeComputed(maxOptimalTime)) {
        setMaxOptimalTime(null);
      }
    }, 1000);

    return (): void => {
      clearInterval(interval);
    };
  }, [maxOptimalTime]);

  const getCheckboxClasses = (): string => {
    if (shouldSendAtBestTime) {
      return styles.checked;
    }

    return `${styles.unchecked} ${scheduledOptimalSendingError ? styles.error : null}`;
  };

  const onClickCheck = (): void => {
    if (!maxOptimalTime) {
      return;
    }

    const newState = !shouldSendAtBestTime;
    onUpdateSendBestTimeToggle(newState);
    setShouldSendatBestTime(newState);
  };

  const getCheckbox = (): ReactElement<any> => {
    return <Checkbox onClick={onClickCheck} className={`${styles.checkbox} ${getCheckboxClasses()}`} size={IconSize.SIZE_ICON_20} isChecked={shouldSendAtBestTime} />;
  };

  const getIconEffects = (): ReactElement<any> => {
    return <Icon className={styles.iconClasses} icon="icon-sparkle" size={IconSize.SIZE_ICON_20} />;
  };

  const onClickInfoIcon = (_e: React.MouseEvent<HTMLElement>): void => {
    clearTimeout(timer.current);
    setIsHoveringOnTooltip(!isHoveringOnTooltip);
  };

  const getInfoTooltipText = (): ReactElement => {
    return (
      <Text
        className={styles.tooltipTextClasses}
        dangerouslySetInnerHTML
        size={TextSize.XXXSMALL}
        val={window.i18next.t('pmwjs_optimal_sending_info_tooltip', {link: sendTimeSupportArticleLink})}
      />
    );
  };

  const handleMouseEnter = (): void => {
    clearTimeout(timer.current);
    setIsHoveringOnTooltip(true);
  };

  const handleMouseLeave = (): void => {
    timer.current = window.setTimeout(() => {
      setIsHoveringOnTooltip(false);
    }, 80);
  };

  useEffect(() => {
    if (!isHoveringOnTooltip) {
      return;
    }

    const icon = infoRef.current;
    const tip = tooltipRef.current;
    if (!icon || !tip) {
      return;
    }

    tip.style.position = 'fixed';
    tip.style.display = 'block';

    const tr = tip.getBoundingClientRect();
    const GAP = 30;

    const trWidth = 150;
    const left = 8 - trWidth;
    const top = 0 - tr.height - GAP;

    tip.style.top = `${top}px`;
    tip.style.left = `${left}px`;
  }, [isHoveringOnTooltip]);

  const getInfoTooltip = (): ReactElement => {
    const icon = (
      <div ref={infoRef} className={`flex-h-row`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={onClickInfoIcon}>
        <Icon onClick={onClickInfoIcon} className={styles.tooltipIcon} icon={'icon-info'} size={IconSize.SIZE_ICON_16} type={IconType.GHOST} />
      </div>
    );

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={
          <div id="tooltip-wrapper">
            <Tooltip
              ref={tooltipRef}
              id={'optimal-sending-tooltip'}
              className={`spacing-m-b-2 flexbox flex-wrap ${styles.tooltipWrapperClasses}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              text={getInfoTooltipText()}
            />
          </div>
        }
        delay={{show: 200, hide: 0}}
        placement="bottom-start"
        show={isHoveringOnTooltip}
        popperConfig={undefined}
        onHide={undefined}
        onToggle={undefined}
        target={undefined}
        flip={undefined}
      >
        {icon}
      </OverlayTrigger>
    );
  };

  const getToggleDescription = (): ReactElement<any> => {
    const text = params.small ? window.i18next.t('pmwjs_best_time') : window.i18next.t('pmwjs_send_best_time');
    return (
      <>
        <Text bold className={`${styles.textDescription} body-md-bold spacing-m-r-2`} val={text} />
        {getInfoTooltip()}
      </>
    );
  };

  const shouldShowBestTimeComputationLoader = (): boolean => {
    if (!shouldSendAtBestTime || scheduledOptimalSendingError) {
      return false;
    }

    return maxOptimalTime === null;
  };

  const getBestTimeComputationLoaderIfNeeded = (): ReactElement<any> | null => {
    if (!shouldShowBestTimeComputationLoader()) {
      return null;
    }

    return (
      <div className={styles.gradientLoader}>
        <Thumbnail
          data={{
            type: ThumbnailType.IMAGE,
            imageSrc: aiGradientSpinner as string,
          }}
          className={styles.gradientSVGSpinner}
        />
      </div>
    );
  };

  const getBestTimeLoaderTextIfNeeded = (): ReactElement<any> | null => {
    if (!shouldShowBestTimeComputationLoader()) {
      return null;
    }

    return (
      <div className="spacing-m-l-3">
        <Text className={styles.textDescriptionLoader} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_calculating_best_time')} />
      </div>
    );
  };

  const getBestTimeIfNeeded = (): ReactElement<any> | null => {
    if (shouldSendAtBestTime && maxOptimalTime) {
      const readableOptimalDateTime = getReadableDateForOptimalSending(maxOptimalTime);

      return (
        <div className={styles.bestTimeDescription}>
          <Text className={`${styles.textDescriptionLoader} ${styles.emailsSentByText}`} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_all_emails_will_be_sent_by')} />
          <Text bold className={styles.textDescriptionLoader} size={TextSize.XSMALL} val={readableOptimalDateTime} />
        </div>
      );
    }

    return null;
  };

  const getClassesForErrorStateIfNeeded = (): string => {
    if (scheduledOptimalSendingError) {
      return styles.error;
    }

    return '';
  };

  const getBestTimeToggle = (): ReactElement<any> => {
    return (
      <div className={styles.bestTimeToggle}>
        <div className={styles.leftPanel}>
          <ClickableDiv className={styles.checkboxClickableDiv} onClick={onClickCheck}>
            {getCheckbox()} {getIconEffects()} {getToggleDescription()}
          </ClickableDiv>
        </div>
        <div className={styles.rightPanel}>
          {getBestTimeComputationLoaderIfNeeded()} {getBestTimeLoaderTextIfNeeded()}
          {getBestTimeIfNeeded()}
        </div>
      </div>
    );
  };

  const getErrorMessagePanel = (): ReactElement<any> | null => {
    if (!scheduledOptimalSendingError || !prefilledDate) {
      return null;
    }

    return (
      <div className={styles.errorMessageTextWrapper}>
        <Text size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_best_time_today')} />
        <Text size={TextSize.XSMALL} bold val={window.i18next.t('pmwjs_passed')} />
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.panelWrapper} ${getClassesForErrorStateIfNeeded()}`}>
      {getBestTimeToggle()}
      {getErrorMessagePanel()}
    </div>
  );
}
